import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { DataTable } from '@plato/ui-legacy/components/list-views/data-table';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { TableHeader } from '@plato/ui-legacy/components/tables/table-header';
import { TitleWithCopyButton } from '@plato/ui-legacy/components/ui/buttons/copy-button';
import { TabsHeader } from '@plato/ui-legacy/components/ui/tabs-header';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { Container } from '@plato/ui/containers/container';
import { DialogBody, DialogHeader } from '@plato/ui/containers/dialog';
import { cn } from '@plato/ui/util/classes';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronLeftIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExplorerTopLevelEntity } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { ExplorerView } from '../../explorer/components/explorer-view.tsx';
import { ExplorerViewProvider, type ExplorerState } from '../../explorer/hooks/use-explorer-query-params.ts';
import { useOrderColumnsForProductDetail } from '../../list-views/columns/use-order-columns-for-product-detail.tsx';
import { useQuoteColumnsForProductDetail } from '../../list-views/columns/use-quote-columns-for-product-detail.tsx';
import { InventoryActions } from './inventory-actions.tsx';
import { InventoryDetailsAvailability } from './inventory-details-availability.tsx';
import { InventoryDetailsDescription } from './inventory-details-description.tsx';
import { InventoryDetailsInfo } from './inventory-details-info.tsx';
import { InventoryDetailsRecommendations } from './inventory-details-recommendations.tsx';
import { InventoryDetailsVariants } from './inventory-details-variants.tsx';

const PageTabs = {
    INFO: 'info',
    AVAILABILITY: 'availability',
    OPEN_QUOTES: 'quotes',
    OPEN_ORDERS: 'orders',
    EXPLORER: 'explorer',
} as const;

type PageTabs = (typeof PageTabs)[keyof typeof PageTabs];

type Props = {
    className?: string;
    erpProductId: string;
    customerId?: string;
    onClose?: () => void;
    onBack?: () => void;
};

function InventoryDetails({ erpProductId, onBack, onClose, className, customerId }: Props) {
    const { t } = useTranslation();
    const { canReadOpenOrders, canReadQuotes, canReadProductVariants } = useAppConfig();
    const [selectedTab, setSelectedTab] = useState<PageTabs>(PageTabs.INFO);
    const [state, setState] = useState<Partial<ExplorerState>>({
        tab: ExplorerTopLevelEntity.CUSTOMER,
    });

    const sendEvent = useAnalytics();
    useEffect(() => {
        sendEvent('item_detail_page_viewed', {
            item_id: erpProductId,
        });
    }, [erpProductId, sendEvent]);

    const ordersColumns = useOrderColumnsForProductDetail();
    const quotesColumns = useQuoteColumnsForProductDetail();

    /* ============================================
     *  API Calls
     * ============================================
     */
    const {
        data: productDetails,
        isLoading: isLoadingDetail,
        isFetched: isFetchedDetail,
    } = api.platoUser.getDetailProduct.getProductDetail.useQuery({
        erpProductId,
    });

    const {
        data: ordersRawData,
        isLoading: isLoadingOrders,
        isFetchingNextPage: isFetchingOrdersNextPage,
        fetchNextPage: fetchNextPageOrders,
        isFetched: isFetchedOrders,
        hasNextPage: hasNextPageOrders,
    } = api.platoUser.getManyQuotesOrdersContainingProduct.getListWithFilters.useInfiniteQuery(
        {
            erpProductId: erpProductId,
            orderTypes: [SrSalesOrderType.ORDER],
        },
        {
            enabled: selectedTab === PageTabs.OPEN_ORDERS,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: ordersCount, isLoading: isLoadingOrdersCount } =
        api.platoUser.getManyQuotesOrdersContainingProduct.getCount.useQuery(
            {
                erpProductId: erpProductId,
                orderTypes: [SrSalesOrderType.ORDER],
            },
            {
                enabled: isFetchedOrders,
            },
        );

    const {
        data: quotesRawData,
        isLoading: isLoadingQuotes,
        isFetchingNextPage: isFetchingQuotesNextPage,
        fetchNextPage: fetchNextPageQuotes,
        hasNextPage: hasNextPageQuotes,
        isFetched: isFetchedQuotes,
    } = api.platoUser.getManyQuotesOrdersContainingProduct.getListWithFilters.useInfiniteQuery(
        {
            erpProductId: erpProductId,
            orderTypes: [SrSalesOrderType.QUOTE],
        },
        {
            enabled: selectedTab === PageTabs.OPEN_QUOTES,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );

    const { data: quotesCount, isLoading: isLoadingQuotesCount } =
        api.platoUser.getManyQuotesOrdersContainingProduct.getCount.useQuery(
            {
                erpProductId: erpProductId,
                orderTypes: [SrSalesOrderType.QUOTE],
            },
            {
                enabled: isFetchedQuotes,
            },
        );

    /* ============================================
     *  Clean Data
     * ============================================
     */

    const quotesData = useMemo(() => {
        if (quotesRawData) {
            return quotesRawData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [quotesRawData]);

    const ordersData = useMemo(() => {
        if (ordersRawData) {
            return ordersRawData.pages.flatMap((page) => page.result);
        } else {
            return [];
        }
    }, [ordersRawData]);

    /* ============================================
     *  Tables
     * ============================================
     */

    const quotesTable = useReactTable({
        data: quotesData,
        columns: quotesColumns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnPinning: { left: ['customer_name'] },
        },
    });

    const ordersTable = useReactTable({
        data: ordersData,
        columns: ordersColumns,
        getCoreRowModel: getCoreRowModel(),
        state: {
            columnPinning: { left: ['customer_name'] },
        },
    });

    /* ============================================
     * Infinite scrolls
     * ============================================
     */

    if (isLoadingDetail || !productDetails) {
        return <LoadingBlockFullPage className="min-h-96" />;
    }

    const title = `${erpProductId} ${productDetails.product_variant_name}`;

    const canReadProductAvailability = typeof productDetails.total_availability === 'number';

    const tabs = [
        PageTabs.INFO,
        ...(canReadProductAvailability ? [PageTabs.AVAILABILITY] : []),
        ...(canReadOpenOrders ? [PageTabs.OPEN_ORDERS] : []),
        ...(canReadQuotes ? [PageTabs.OPEN_QUOTES] : []),
        PageTabs.EXPLORER,
    ];

    return (
        <Container
            level={1}
            className={cn('flex h-full min-h-0 w-full grow flex-col bg-neutral-50', className)}
        >
            {/* The tabs' own rule is the band's bottom edge, so the header suppresses its own. */}
            <DialogHeader className="flex-col items-stretch border-b-0 bg-white pb-0">
                <div className="flex items-center gap-default">
                    {onBack && (
                        <IconButton label={t('back', { ns: 'common' })} onClick={onBack}>
                            <ChevronLeftIcon />
                        </IconButton>
                    )}
                    <h1 className="flex min-w-0 grow flex-row items-center text-heading-3">
                        <TitleWithCopyButton
                            text={title}
                            textToCopy={title}
                            label={t('copy_id_name_tooltip', { ns: 'common' })}
                        />
                    </h1>
                    <InventoryActions itemTitle={title} customerId={customerId} />
                    {onClose && (
                        <IconButton label={t('form__close', { ns: 'common' })} onClick={onClose}>
                            <XIcon />
                        </IconButton>
                    )}
                </div>
                <TabsHeader<PageTabs>
                    selectedTabKey={selectedTab}
                    onTabChange={(value) => {
                        setSelectedTab(value);
                    }}
                    tabs={tabs}
                    getTabLabel={(tab) => t(`tab_title__${tab}`, { ns: 'product-details' })}
                />
            </DialogHeader>
            <DialogBody className="flex flex-col gap-container-cozy py-container-cozy">
                {selectedTab === PageTabs.INFO && (
                    <>
                        <InventoryDetailsInfo data={productDetails} />
                        <InventoryDetailsDescription data={productDetails} />
                        <InventoryDetailsRecommendations data={productDetails} />
                        {canReadProductVariants && (
                            <InventoryDetailsVariants
                                erpProductId={erpProductId}
                                fetchEnabled={isFetchedDetail}
                            />
                        )}
                    </>
                )}
                {selectedTab === PageTabs.AVAILABILITY && (
                    <InventoryDetailsAvailability erpProductId={erpProductId} />
                )}
                {selectedTab === PageTabs.OPEN_ORDERS && (
                    <>
                        <DataTable
                            isLoading={isLoadingOrders}
                            isFetchingNextPage={isFetchingOrdersNextPage}
                            onFetchNextPage={fetchNextPageOrders}
                            hasNextPage={hasNextPageOrders}
                            totalRowsCount={ordersCount}
                            table={ordersTable}
                            columns={ordersColumns}
                        />
                        <TableHeader count={ordersCount} isLoadingCount={isLoadingOrdersCount} />
                    </>
                )}
                {selectedTab === PageTabs.OPEN_QUOTES && (
                    <>
                        <DataTable
                            isLoading={isLoadingQuotes}
                            isFetchingNextPage={isFetchingQuotesNextPage}
                            onFetchNextPage={fetchNextPageQuotes}
                            hasNextPage={hasNextPageQuotes}
                            totalRowsCount={quotesCount}
                            table={quotesTable}
                            columns={quotesColumns}
                        />
                        <TableHeader count={quotesCount} isLoadingCount={isLoadingQuotesCount} />
                    </>
                )}
                {selectedTab === PageTabs.EXPLORER && (
                    <ExplorerViewProvider
                        value={{
                            state,
                            setState,
                            defaultFilters: {
                                product: [erpProductId],
                            },
                        }}
                    >
                        <ExplorerView pageSource="product" />
                    </ExplorerViewProvider>
                )}
            </DialogBody>
        </Container>
    );
}

export default InventoryDetails;
